import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';

import { getDockPillSx } from '../dock';

export interface PaginationDockProps {
  page: number;
  /** Se normaliza internamente a mínimo 1 si es undefined o 0 */
  totalPages?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  /** Muestra skeleton en la zona de números mientras carga (ej. al cambiar de página) */
  loading?: boolean;
  sx?: object;
}

const ELLIPSIS = '…';

function getPageNumbers(page: number, totalPages: number): (number | string)[] {
  if (totalPages <= 0) return [];
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | string)[] = [1];
  if (page <= 3) {
    pages.push(2, 3);
    if (totalPages > 4) pages.push(ELLIPSIS);
    pages.push(totalPages);
  } else if (page >= totalPages - 2) {
    pages.push(ELLIPSIS);
    pages.push(totalPages - 2, totalPages - 1, totalPages);
  } else {
    pages.push(ELLIPSIS, page, ELLIPSIS, totalPages);
  }
  return pages;
}

const PageNumbers: React.FC<{
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}> = ({ page, totalPages, onPageChange, disabled }) => {
  const items = getPageNumbers(page, totalPages);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        minWidth: 0,
      }}
    >
      {items.map((item, index) =>
        item === ELLIPSIS ? (
          <Typography
            key={`ellipsis-${index}`}
            component="span"
            sx={{
              color: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 400,
            }}
          >
            {ELLIPSIS}
          </Typography>
        ) : (
          <Typography
            key={item}
            component="span"
            onClick={disabled ? undefined : () => onPageChange(item as number)}
            sx={{
              color: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: page === item ? 700 : 400,
              cursor: disabled ? 'default' : 'pointer',
              opacity: page === item ? 1 : 0.85,
              '&:hover': disabled ? {} : { textDecoration: 'underline' },
            }}
          >
            {item}
          </Typography>
        ),
      )}
    </Box>
  );
};

const PaginationDock: React.FC<PaginationDockProps> = ({
  page,
  totalPages: totalPagesProp,
  onPageChange,
  disabled = false,
  loading = false,
  sx,
}) => {
  const totalPages = Math.max(1, totalPagesProp ?? 1);
  const isDisabled = disabled || loading;
  const canGoPrev = page > 1 && !isDisabled;
  const canGoNext = page < totalPages && !isDisabled;

  return (
    <Stack
      direction="row"
      sx={{
        ...getDockPillSx(),
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 1.25,
        width: 'fit-content',
        ...sx,
      }}
    >
      <IconButton
        onClick={() => onPageChange(page - 1)}
        disabled={!canGoPrev}
        aria-label="Página anterior"
        size="small"
        sx={{
          color: 'primary.main',
          flexShrink: 0,
          '&.Mui-disabled': { color: 'action.disabled' },
        }}
      >
        <ArrowBackIosIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Skeleton
            variant="text"
            width={24}
            height={20}
            sx={{ fontSize: '0.875rem' }}
          />
          <Skeleton
            variant="text"
            width={24}
            height={20}
            sx={{ fontSize: '0.875rem' }}
          />
          <Skeleton
            variant="text"
            width={24}
            height={20}
            sx={{ fontSize: '0.875rem' }}
          />
        </Box>
      ) : (
        <PageNumbers
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          disabled={disabled}
        />
      )}

      <IconButton
        onClick={() => onPageChange(page + 1)}
        disabled={!canGoNext}
        aria-label="Página siguiente"
        size="small"
        sx={{
          color: 'primary.main',
          flexShrink: 0,
          '&.Mui-disabled': { color: 'action.disabled' },
        }}
      >
        <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Stack>
  );
};

export default PaginationDock;
